import { Icon, Button, Form, RadioLabel, TranslateDropdown, Input, Modal, CurrencyInputLabel, Counter, CheckboxLabel, CounterLabel } from '@weezevent/nacre';
import React from 'react';
import Style from './editCrossSelling.module.css';
import Styles from '../../../styles/rate/groupModal.module.css';
import { useTranslation } from 'react-i18next';
import LoadingCard from '../../../components/loadingCard';
import useAppContext from '../../../context/useAppContext';
import { getProductsLanguages, getDefaultLanguage, getLanguagesTranslateDropdown, removeDefaultLanguage } from './list/translationUtils';
import i18n from 'i18next';

export default function VariantModal({
    variants,
    open,
    onClose = () => {},
    submitting,
    form,
    quotas,
    setForm,
    setFormObject,
    productCategory = 0,
    errorsClick,
    setErrorsClick,
    handleEdit,
    handleSave
}) {
    const { t } = useTranslation();
    const [{ organization }] = useAppContext();
    const locale = i18n.language;

    const [unlimitedQuantity, setUnlimitedQuantity] = React.useState(form.id ? !quotas.find(q => q.variant === form.id)?.quota_total : true);

    const productsLanguages = React.useMemo(() => getProductsLanguages(organization), [organization]);
    const defaultLanguage = React.useMemo(() => getDefaultLanguage(organization, productsLanguages), [organization, productsLanguages]);
    const languagesTranslateDropdown = React.useMemo(() => getLanguagesTranslateDropdown(productsLanguages), [productsLanguages]);

    const hasNameTranslations = React.useMemo(() => {
        const noEmptyTranslation = Object.values(form.translations.name).filter(key => key);
        if (!('translations' in form) || !('name' in form.translations) || noEmptyTranslation.length === 0) {
            return false;
        }
        return defaultLanguage in form.translations.name && Object.keys(noEmptyTranslation).length > 1;
    }, [form, defaultLanguage]);

    const handleSaveEdit = React.useCallback(
        (value = null) => {
            if (form.id) {
                handleEdit(
                    {
                        id: form.id,
                        reference: form.reference,
                        disabled: form.disabled,
                        price: form.price,
                        name: form.name,
                        quota: form.quota,
                        ordering: form.ordering,
                        translations: removeDefaultLanguage(form.translations, defaultLanguage)
                    },
                    form.id,
                    value
                );
            } else {
                handleSave(
                    {
                        reference: form.reference,
                        disabled: form.disabled,
                        price: form.price,
                        ordering: form.ordering,
                        transaction_total_count: 0,
                        name: form.name,
                        quota: form.quota,
                        translations: removeDefaultLanguage(form.translations, defaultLanguage)
                    },
                    value
                );
            }
        },
        [form, variants, handleEdit, handleSave, defaultLanguage]
    );

    const isInvalidForm = React.useMemo(() => {
        return !form.name;
    }, [form, defaultLanguage]);

    const errorName = React.useCallback(() => {
        return isInvalidForm && errorsClick.name;
    }, [isInvalidForm, errorsClick]);

    return (
        <Modal
            className={Styles['group-modal']}
            size={'large'}
            open={open}
            allowExternalClosing={false}
            onClose={() => {
                onClose();
            }}
            title={form.id ? t('ticket.cross-selling.modalVariant.titleUpdate') : t('ticket.cross-selling.modalVariant.title')}
            subtitle={form.id ? t('ticket.cross-selling.modalVariant.subtitleUpdate') : t('ticket.cross-selling.modalVariant.subtitle')}
            submitButton={
                <>
                    <Button
                        primary
                        inverted
                        disabled={isInvalidForm && !hasNameTranslations}
                        onClick={() => {
                            setErrorsClick({ price: true, name: false });
                            handleSaveEdit('new');
                        }}
                        label={!form.id ? t('ticket.cross-selling.modalVariant.addNewVariant') : t('ticket.cross-selling.modalVariant.updateAddNewVariant')}
                    />
                    <Button
                        primary
                        disabled={isInvalidForm && !hasNameTranslations}
                        onClick={() => {
                            setErrorsClick({ price: true, name: true });
                            handleSaveEdit();
                        }}
                        label={!form.id ? t('ticket.common.action.add') : t('ticket.common.action.update')}
                    />
                </>
            }
            cancelButton={
                <Button
                    onClick={() => {
                        onClose();
                    }}
                    label={t('ticket.common.action.cancel')}
                />
            }
        >
            <LoadingCard loading={submitting}>
                <Form.Field>
                    <VariantNameInput
                        formState={form}
                        setFormObject={setFormObject}
                        defaultLanguage={defaultLanguage}
                        languageOptions={languagesTranslateDropdown}
                        errorName={errorName}
                    />
                </Form.Field>

                <Form.Field>
                    <Form.Label label={t('ticket.cross-selling.modalVariant.ref')} optionalText={t('ticket.common.labelOptional')} />
                    <Input value={form.reference} onChange={event => setFormObject({ ...form, reference: event.target.value })} />
                </Form.Field>

                <Form.Field>
                    <CurrencyInputLabel
                        key="commission"
                        style={{ width: '137px' }}
                        label={t('ticket.discountForm.grid.tarif_ttc')}
                        currency={organization.currency}
                        locale={locale}
                        numberDecimals={2}
                        value={form.price}
                        onChange={({ value }) => setFormObject({ ...form, price: value })}
                    />
                </Form.Field>

                <span className={Style['add-cross-modal-variant-costs-text']}>{`${t('ticket.cross-selling.modalVariant.frais')} ${productCategory === 1 ? '1.25' : '2.5'} %`}</span>

                <Form.Field>
                    <Form.Label label={t('ticket.cross-selling.modalVariant.quotas')} optionalText={t('ticket.common.labelOptional')} />
                    <div className={Style['add-cross-modal-variant-quota-container']}>
                        <Counter
                            disabled={unlimitedQuantity}
                            numberDecimals={0}
                            min={1}
                            onChange={(e, value) => setForm('quota', value)}
                            step={1}
                            value={form.quota || 0}
                            hideArrows={true}
                        />
                        <CheckboxLabel
                            className={Style['add-cross-modal-variant-quota-checkbox']}
                            name="checkboxQuota"
                            label={t('ticket.cross-selling.modalVariant.illimitedQuantity')}
                            checked={unlimitedQuantity}
                            onChange={() => {
                                setUnlimitedQuantity(!unlimitedQuantity);
                                setFormObject({ ...form, quota: unlimitedQuantity ? 0 : null });
                            }}
                        />
                    </div>
                </Form.Field>

                <Form.Field>
                    <CounterLabel
                        label={t('ticket.charts.newInventory.display_order')}
                        max={999}
                        numberDecimals={0}
                        min={1}
                        onChange={(e, value) => setForm('ordering', value - 1)}
                        step={1}
                        value={form.ordering + 1}
                    />
                </Form.Field>

                <Form.Field>
                    <Form.Label label={t('ticket.cross-selling.modalVariant.visibilityOnlineTitle')} />
                    <RadioLabel
                        checked={!form.disabled}
                        active={!form.disabled}
                        onClick={() => {
                            setFormObject({ ...form, disabled: false });
                        }}
                        name="visible-variant"
                        value="variant-visibility-1"
                        inline
                        bordered
                    >
                        {t('ticket.cross-selling.modalVariant.visibilityOnline')} {<Icon name="eye" />}
                    </RadioLabel>
                    <RadioLabel
                        active={form.disabled}
                        checked={form.disabled}
                        onClick={() => {
                            setFormObject({ ...form, disabled: true });
                        }}
                        name="visible-variant"
                        value="variant-visibility-2"
                        inline
                        bordered
                    >
                        {t('ticket.common.action.hide-online')} {<Icon name="eye-slash" />}
                    </RadioLabel>
                </Form.Field>
            </LoadingCard>
        </Modal>
    );
}

function VariantNameInput({ formState, setFormObject, defaultLanguage, languageOptions, errorName }) {
    const { t } = useTranslation();
    const [selectedNameLanguage, setSelectedNameLanguage] = React.useState(defaultLanguage);

    const handleTranslations = React.useCallback(
        value => {
            let ret = { ...formState };
            ret.translations.name[selectedNameLanguage] = value || '';
            if (selectedNameLanguage === defaultLanguage) ret.name = value;
            setFormObject(ret);
        },
        [setFormObject, formState, defaultLanguage, selectedNameLanguage]
    );

    return (
        <>
            <Form.Label className={Styles['remove-margin-bottom']}>
                <div className={Styles['group-label-input']}>
                    {t('ticket.cross-selling.modalVariant.nameItem')}
                    <TranslateDropdown
                        defaultValue={defaultLanguage}
                        languages={languageOptions}
                        onChange={e => setSelectedNameLanguage(e)}
                        selected={Object.entries(formState.translations.name)
                            .map(([k, v]) => (v ? k : undefined))
                            .filter(e => e)}
                    />
                </div>
            </Form.Label>
            <Form.Label className={Styles['group-modal-form-label-subtitle']}>{t(`ticket.rates.groupModal.groupNameSubtitle`)}</Form.Label>
            <Form.Input value={formState.translations.name[selectedNameLanguage] || ''} onChange={({ target: { value } }) => handleTranslations(value)} errors={errorName()} />
        </>
    );
}
